import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { format, subDays } from 'date-fns';
import {
  FileText,
  Download,
  Loader2,
  Calendar as CalendarIcon,
  GripVertical,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  Filter,
  BarChart3,
  Table,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Wrench,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Save,
  FolderOpen,
  Trash2,
  Edit3,
} from 'lucide-react';
import { cn, formatNumber, formatNumberExact, formatCurrency, SUPPORTED_CURRENCIES } from '../lib/utils';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import DV360BreakdownTabs from '../components/DV360BreakdownTabs';
import DV360ComprehensiveReport from '../components/DV360ComprehensiveReport';

// Meta/Facebook Metrics based on Facebook Marketing API
// NOTE: Video metrics are NOT available when using Age or Gender breakdowns (Meta API limitation)
const META_METRICS = [
  // Basic Performance - Available with all breakdowns
  { id: 'date', label: 'Date', type: 'text', category: 'Dimensions' },
  { id: 'ad_set_name', label: 'Ad Set Name', type: 'text', category: 'Dimensions' },
  { id: 'ad_name', label: 'Ad Name', type: 'text', category: 'Dimensions' },
  { id: 'impressions', label: 'Impressions', type: 'number', category: 'Performance' },
  { id: 'clicks', label: 'Clicks (All)', type: 'number', category: 'Performance' },
  { id: 'spend', label: 'Amount Spent', type: 'currency', category: 'Performance' },
  { id: 'reach', label: 'Reach', type: 'number', category: 'Performance' },
  { id: 'frequency', label: 'Frequency', type: 'decimal', category: 'Performance' },
  
  // Engagement - Available with all breakdowns
  { id: 'link_clicks', label: 'Link Clicks', type: 'number', category: 'Engagement' },
  { id: 'post_engagement', label: 'Post Engagement', type: 'number', category: 'Engagement' },
  { id: 'page_engagement', label: 'Page Engagement', type: 'number', category: 'Engagement' },
  { id: 'post_reactions', label: 'Post Reactions', type: 'number', category: 'Engagement' },
  { id: 'post_comments', label: 'Post Comments', type: 'number', category: 'Engagement' },
  { id: 'post_shares', label: 'Post Shares', type: 'number', category: 'Engagement' },
  { id: 'post_saves', label: 'Post Saves', type: 'number', category: 'Engagement' },
  { id: 'page_likes', label: 'Page Likes', type: 'number', category: 'Engagement' },
  
  // Video - NOT available with Age/Gender breakdowns (Meta API limitation)
  { id: 'video_views', label: 'Video Views', type: 'number', category: 'Video', noBreakdown: true },
  { id: 'video_thruplay', label: 'ThruPlay', type: 'number', category: 'Video', noBreakdown: true },
  { id: 'video_p25_watched', label: 'Video 25% Watched', type: 'number', category: 'Video', noBreakdown: true },
  { id: 'video_p50_watched', label: 'Video 50% Watched', type: 'number', category: 'Video', noBreakdown: true },
  { id: 'video_p75_watched', label: 'Video 75% Watched', type: 'number', category: 'Video', noBreakdown: true },
  { id: 'video_p95_watched', label: 'Video 95% Watched', type: 'number', category: 'Video', noBreakdown: true },
  { id: 'video_p100_watched', label: 'Video 100% Watched', type: 'number', category: 'Video', noBreakdown: true },
  { id: 'video_avg_time_watched', label: 'Avg Video Watch Time', type: 'number', category: 'Video', noBreakdown: true },
  
  // Cost Metrics
  { id: 'cpc', label: 'CPC (All)', type: 'currency', category: 'Cost' },
  { id: 'cpm', label: 'CPM', type: 'currency', category: 'Cost' },
  { id: 'cpp', label: 'Cost per 1K Reach', type: 'currency', category: 'Cost' },
  { id: 'ctr', label: 'CTR (All)', type: 'percent', category: 'Cost' },
  { id: 'link_ctr', label: 'Link CTR', type: 'percent', category: 'Cost' },
  { id: 'cost_per_link_click', label: 'Cost per Link Click', type: 'currency', category: 'Cost' },
  { id: 'cost_per_thruplay', label: 'Cost per ThruPlay', type: 'currency', category: 'Cost' },
  
  // Conversions
  { id: 'conversions', label: 'Conversions', type: 'number', category: 'Conversions' },
  { id: 'conversion_rate', label: 'Conversion Rate', type: 'percent', category: 'Conversions' },
  { id: 'cost_per_conversion', label: 'Cost per Conversion', type: 'currency', category: 'Conversions' },
  { id: 'conversion_value', label: 'Conversion Value', type: 'currency', category: 'Conversions' },
  { id: 'roas', label: 'ROAS', type: 'decimal', category: 'Conversions' },
  { id: 'purchases', label: 'Purchases', type: 'number', category: 'Conversions' },
  { id: 'purchase_value', label: 'Purchase Value', type: 'currency', category: 'Conversions' },
  { id: 'add_to_cart', label: 'Add to Cart', type: 'number', category: 'Conversions' },
  { id: 'initiate_checkout', label: 'Initiate Checkout', type: 'number', category: 'Conversions' },
  { id: 'leads', label: 'Leads', type: 'number', category: 'Conversions' },
  { id: 'cost_per_lead', label: 'Cost per Lead', type: 'currency', category: 'Conversions' },
  
  // App
  { id: 'app_installs', label: 'App Installs', type: 'number', category: 'App' },
  { id: 'cost_per_app_install', label: 'Cost per App Install', type: 'currency', category: 'App' },
  
  // Quality
  { id: 'quality_ranking', label: 'Quality Ranking', type: 'text', category: 'Quality' },
  { id: 'engagement_rate_ranking', label: 'Engagement Rate Ranking', type: 'text', category: 'Quality' },
  { id: 'conversion_rate_ranking', label: 'Conversion Rate Ranking', type: 'text', category: 'Quality' },
];

// Meta Breakdown Options from Facebook API
// Meta breakdowns - only show those that are actively synced from API
// Supported breakdowns: date, campaign, ad_account, age, gender, country, region
// Note: Meta API doesn't allow combining breakdowns - each requires separate API calls
const META_BREAKDOWNS = [
  { id: 'date', label: 'Date', supported: true },
  { id: 'campaign', label: 'Campaign', supported: true },
  { id: 'platform', label: 'Platform', supported: true, note: 'Platform breakdown' },
  { id: 'age', label: 'Age', supported: true, note: 'Demographic breakdown from Meta API' },
  { id: 'gender', label: 'Gender', supported: true, note: 'Demographic breakdown from Meta API' },
  { id: 'country', label: 'Country', supported: true, note: 'Geographic breakdown from Meta API' },
  { id: 'region', label: 'Region', supported: true, note: 'Geographic breakdown from Meta API' },
  // The following breakdowns require additional Meta API calls to fetch data
  { id: 'publisher_platform', label: 'Publisher Platform', supported: false, note: 'Requires placement breakdown sync' },
  { id: 'device_platform', label: 'Device Platform', supported: false, note: 'Requires device breakdown sync' },
  { id: 'impression_device', label: 'Impression Device', supported: false, note: 'Requires device breakdown sync' },
];

// Platform-specific metrics configuration
const PLATFORM_METRICS = {
  all: [
    { id: 'date', label: 'Date', type: 'text', category: 'Dimensions' },
    { id: 'impressions', label: 'Impressions', type: 'number', category: 'Performance' },
    { id: 'clicks', label: 'Clicks', type: 'number', category: 'Performance' },
    { id: 'spend', label: 'Spend', type: 'currency', category: 'Performance' },
    { id: 'ctr', label: 'CTR (%)', type: 'percent', category: 'Cost' },
    { id: 'cpc', label: 'CPC', type: 'currency', category: 'Cost' },
    { id: 'cpm', label: 'CPM', type: 'currency', category: 'Cost' },
    { id: 'conversions', label: 'Conversions', type: 'number', category: 'Conversions' },
    { id: 'reach', label: 'Reach', type: 'number', category: 'Performance' },
  ],
  Meta: META_METRICS,
  DV360: [
    { id: 'date', label: 'Date', type: 'text', category: 'Dimensions' },
    { id: 'insertion_order_name', label: 'Insertion Order', type: 'text', category: 'Dimensions' },
    { id: 'line_item_name', label: 'Line Item', type: 'text', category: 'Dimensions' },
    { id: 'creative_name', label: 'Creative', type: 'text', category: 'Dimensions' },
    { id: 'source', label: 'Source', type: 'text', category: 'Dimensions' },
    { id: 'domain', label: 'Domain', type: 'text', category: 'Dimensions' },
    { id: 'impressions', label: 'Impressions', type: 'number', category: 'Performance' },
    { id: 'clicks', label: 'Clicks', type: 'number', category: 'Performance' },
    { id: 'spend', label: 'Media Cost', type: 'currency', category: 'Performance' },
    { id: 'total_media_cost', label: 'Total Media Cost', type: 'currency', category: 'Performance' },
    { id: 'reach', label: 'Reach', type: 'number', category: 'Performance' },
    { id: 'ctr', label: 'CTR (%)', type: 'percent', category: 'Cost' },
    { id: 'cpc', label: 'CPC', type: 'currency', category: 'Cost' },
    { id: 'cpm', label: 'CPM', type: 'currency', category: 'Cost' },
    { id: 'conversions', label: 'Conversions', type: 'number', category: 'Conversions' },
    { id: 'total_conversions', label: 'Total Conversions', type: 'number', category: 'Conversions' },
    { id: 'post_click_conversions', label: 'Post-Click Conversions', type: 'number', category: 'Conversions' },
    { id: 'post_view_conversions', label: 'Post-View Conversions', type: 'number', category: 'Conversions' },
    { id: 'viewable_impressions', label: 'Viewable Impressions', type: 'number', category: 'Viewability' },
    { id: 'measurable_impressions', label: 'Measurable Impressions', type: 'number', category: 'Viewability' },
    { id: 'viewability_rate', label: 'Viewability Rate (%)', type: 'percent', category: 'Viewability' },
    { id: 'active_view_viewable_impressions', label: 'Active View Viewable', type: 'number', category: 'Viewability' },
    { id: 'active_view_measurable_impressions', label: 'Active View Measurable', type: 'number', category: 'Viewability' },
    { id: 'active_view_viewable_rate', label: 'Active View Rate (%)', type: 'percent', category: 'Viewability' },
    { id: 'video_starts', label: 'Video Starts', type: 'number', category: 'Video' },
    { id: 'video_first_quartile', label: 'Video 25%', type: 'number', category: 'Video' },
    { id: 'video_midpoint', label: 'Video 50%', type: 'number', category: 'Video' },
    { id: 'video_third_quartile', label: 'Video 75%', type: 'number', category: 'Video' },
    { id: 'video_completions', label: 'Video Completions', type: 'number', category: 'Video' },
    { id: 'video_completion_rate', label: 'Video Completion Rate (%)', type: 'percent', category: 'Video' },
    { id: 'rich_media_engagements', label: 'Rich Media Engagements', type: 'number', category: 'Engagement' },
    { id: 'revenue', label: 'Revenue', type: 'currency', category: 'Revenue' },
  ],
  Mondiads: [
    { id: 'date', label: 'Date', type: 'text', category: 'Dimensions' },
    { id: 'impressions', label: 'Impressions', type: 'number', category: 'Performance' },
    { id: 'clicks', label: 'Clicks', type: 'number', category: 'Performance' },
    { id: 'spend', label: 'Spend', type: 'currency', category: 'Performance' },
    { id: 'ctr', label: 'CTR (%)', type: 'percent', category: 'Cost' },
    { id: 'cpc', label: 'CPC', type: 'currency', category: 'Cost' },
    { id: 'cpm', label: 'CPM', type: 'currency', category: 'Cost' },
  ],
  Clickadu: [
    { id: 'date', label: 'Date', type: 'text', category: 'Dimensions' },
    { id: 'impressions', label: 'Impressions', type: 'number', category: 'Performance' },
    { id: 'clicks', label: 'Clicks', type: 'number', category: 'Performance' },
    { id: 'spend', label: 'Spend', type: 'currency', category: 'Performance' },
    { id: 'ctr', label: 'CTR (%)', type: 'percent', category: 'Cost' },
    { id: 'cpc', label: 'CPC', type: 'currency', category: 'Cost' },
    { id: 'cpm', label: 'CPM', type: 'currency', category: 'Cost' },
    { id: 'conversions', label: 'Conversions', type: 'number', category: 'Conversions' },
  ],
  Propeller: [
    { id: 'date', label: 'Date', type: 'text', category: 'Dimensions' },
    { id: 'impressions', label: 'Impressions', type: 'number', category: 'Performance' },
    { id: 'clicks', label: 'Clicks', type: 'number', category: 'Performance' },
    { id: 'spend', label: 'Spend', type: 'currency', category: 'Performance' },
    { id: 'ctr', label: 'CTR (%)', type: 'percent', category: 'Cost' },
    { id: 'cpc', label: 'CPC', type: 'currency', category: 'Cost' },
    { id: 'cpm', label: 'CPM', type: 'currency', category: 'Cost' },
  ],
};

// Breakdown options per platform
const BREAKDOWN_OPTIONS = {
  all: [
    { id: 'date', label: 'Date' },
    { id: 'campaign', label: 'Campaign' },
    { id: 'platform', label: 'Platform' },
    { id: 'ad_account', label: 'Ad Account' },
  ],
  Meta: META_BREAKDOWNS,
  DV360: [
    { id: 'date', label: 'Date', supported: true },
    { id: 'campaign', label: 'Campaign', supported: true },
    { id: 'insertion_order', label: 'Insertion Order', supported: true },
    { id: 'line_item', label: 'Line Item', supported: true },
    { id: 'creative', label: 'Creative', supported: true },
    { id: 'ad_account', label: 'Advertiser', supported: true },
    { id: 'device_type', label: 'Device Type', supported: true, note: 'Desktop, Mobile, Tablet, Connected TV' },
    { id: 'browser', label: 'Browser', supported: true },
    { id: 'operating_system', label: 'Operating System', supported: true },
    { id: 'country', label: 'Country', supported: true },
    { id: 'region', label: 'Region', supported: true },
    { id: 'city', label: 'City', supported: true },
    { id: 'exchange', label: 'Exchange', supported: true },
    { id: 'media_type', label: 'Media Type', supported: true, note: 'Display, Video, Audio' },
    { id: 'creative_size', label: 'Creative Size', supported: true },
    { id: 'age', label: 'Age', supported: true, note: '18-24, 25-34, 35-44, 45-54, 55-64, 65+' },
    { id: 'gender', label: 'Gender', supported: true, note: 'Male, Female, Unknown' },
  ],
  Mondiads: [
    { id: 'date', label: 'Date' },
    { id: 'campaign', label: 'Campaign' },
  ],
  Clickadu: [
    { id: 'date', label: 'Date' },
    { id: 'campaign', label: 'Campaign' },
  ],
  Propeller: [
    { id: 'date', label: 'Date' },
    { id: 'campaign', label: 'Campaign' },
  ],
};

const PLATFORMS = ['All Platforms', 'Meta', 'DV360', 'Mondiads', 'Clickadu', 'Propeller'];

// Platform display names
const PLATFORM_NAMES = {
  'all': 'All Platforms',
  'meta': 'Meta',
  'dv360': 'DV360',
  'mondiads': 'Mondiads',
  'clickadu': 'Clickadu',
  'propeller': 'Propeller'
};

export const PerformanceReport = () => {
  const { user, advertiser } = useAuth();
  const { platform: urlPlatform } = useParams();
  const navigate = useNavigate();
  
  // Determine the selected platform from URL
  const platformFromUrl = urlPlatform ? PLATFORM_NAMES[urlPlatform.toLowerCase()] || 'All Platforms' : 'All Platforms';
  
  // Get advertiser's currency (default to USD)
  const advertiserCurrency = advertiser?.currency || 'USD';
  
  // State
  const [generating, setGenerating] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [fixingData, setFixingData] = useState(false);
  const [fixingCurrency, setFixingCurrency] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  
  // Auto resync state
  const [autoResyncEnabled, setAutoResyncEnabled] = useState(true);
  const [lastAutoSync, setLastAutoSync] = useState(null);
  const [nextAutoSync, setNextAutoSync] = useState(null);
  const AUTO_RESYNC_INTERVAL = 10 * 60 * 1000; // 10 minutes in milliseconds
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // Sorting
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Filters
  const [selectedPlatform, setSelectedPlatform] = useState(platformFromUrl);
  const [selectedAdAccount, setSelectedAdAccount] = useState('All Ad Accounts');
  const [selectedAdSets, setSelectedAdSets] = useState([]);
  const [selectedAds, setSelectedAds] = useState([]);
  const [adSets, setAdSets] = useState([]);
  const [ads, setAds] = useState([]);
  const [adSetSearch, setAdSetSearch] = useState('');
  const [adSearch, setAdSearch] = useState('');
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [campaignSearch, setCampaignSearch] = useState('');
  const [adAccountSearch, setAdAccountSearch] = useState('');
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  
  // DV360-specific filters
  const [insertionOrders, setInsertionOrders] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [selectedInsertionOrders, setSelectedInsertionOrders] = useState([]);
  const [selectedLineItems, setSelectedLineItems] = useState([]);
  const [insertionOrderSearch, setInsertionOrderSearch] = useState('');
  const [lineItemSearch, setLineItemSearch] = useState('');
  
  // Metrics & Breakdown
  const [selectedMetrics, setSelectedMetrics] = useState(['date', 'impressions', 'clicks', 'spend', 'ctr', 'cpc', 'cpm']);
  const [selectedBreakdown, setSelectedBreakdown] = useState('campaign');
  const [includeDate, setIncludeDate] = useState(true);
  const [includeCampaignName, setIncludeCampaignName] = useState(true);
  
  // Report Templates
  const [templates, setTemplates] = useState([]);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [showLoadTemplateDialog, setShowLoadTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  
  // Column arrangement and drag-and-drop
  const [columnOrder, setColumnOrder] = useState([]);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  
  // Get available metrics based on platform
  const availableMetrics = useMemo(() => {
    const platform = selectedPlatform === 'All Platforms' ? 'all' : selectedPlatform;
    return PLATFORM_METRICS[platform] || PLATFORM_METRICS.all;
  }, [selectedPlatform]);
  
  // Group metrics by category
  const metricsByCategory = useMemo(() => {
    const grouped = {};
    availableMetrics.forEach(m => {
      const cat = m.category || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(m);
    });
    return grouped;
  }, [availableMetrics]);
  
  // Get available breakdowns based on platform
  const availableBreakdowns = useMemo(() => {
    const platform = selectedPlatform === 'All Platforms' ? 'all' : selectedPlatform;
    return BREAKDOWN_OPTIONS[platform] || BREAKDOWN_OPTIONS.all;
  }, [selectedPlatform]);
  
  // Filter campaigns by search and ad account
  const filteredCampaigns = useMemo(() => {
    let filtered = campaigns;
    
    // Filter by ad account
    if (selectedAdAccount !== 'All Ad Accounts') {
      filtered = filtered.filter(c => c.ad_account_name === selectedAdAccount);
    }
    
    // Filter by search
    if (campaignSearch) {
      const search = campaignSearch.toLowerCase();
      filtered = filtered.filter(c => 
        c.name.toLowerCase().includes(search) ||
        c.platform?.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  }, [campaigns, campaignSearch, selectedAdAccount]);
  
  // Filter ad sets by search
  const filteredAdSets = useMemo(() => {
    if (!adSetSearch) return adSets;
    const search = adSetSearch.toLowerCase();
    return adSets.filter(a => 
      a.name?.toLowerCase().includes(search)
    );
  }, [adSets, adSetSearch]);
  
  // Filter ads by search and selected ad sets
  const filteredAds = useMemo(() => {
    let filtered = ads;
    
    // Filter by selected ad sets
    if (selectedAdSets.length > 0) {
      filtered = filtered.filter(a => selectedAdSets.includes(a.ad_set_name));
    }
    
    // Filter by search
    if (adSearch) {
      const search = adSearch.toLowerCase();
      filtered = filtered.filter(a => 
        a.name?.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  }, [ads, adSearch, selectedAdSets]);
  
  // Sorted data
  const sortedData = useMemo(() => {
    if (!sortColumn || !reportData.length) return reportData;
    
    return [...reportData].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      
      // Handle numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle string comparison
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();
      
      if (sortDirection === 'asc') {
        return aVal.localeCompare(bVal);
      }
      return bVal.localeCompare(aVal);
    });
  }, [reportData, sortColumn, sortDirection]);
  
  // Paginated data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);
  
  // Total pages
  const totalPages = useMemo(() => {
    return Math.ceil(sortedData.length / pageSize);
  }, [sortedData.length, pageSize]);
  
  // Fetch campaigns on mount and when platform/ad account changes
  useEffect(() => {
    fetchCampaigns();
  }, [selectedPlatform, selectedAdAccount]);
  
  // Fetch ad accounts and sync status when platform changes
  useEffect(() => {
    fetchAdAccounts();
    fetchSyncStatus();
    // Reset ad account selection when platform changes
    setSelectedAdAccount('All Ad Accounts');
    setSelectedCampaigns([]);
    setSelectedAdSets([]);
    setSelectedAds([]);
  }, [selectedPlatform]);
  
  // Fetch ad sets when ad account or campaigns change
  useEffect(() => {
    fetchAdSets();
    // Reset ad set selection when dependencies change
    setSelectedAdSets([]);
  }, [selectedAdAccount, selectedPlatform, selectedCampaigns]);
  
  // Fetch ads when ad sets or campaigns change
  useEffect(() => {
    fetchAds();
    // Reset ad selection when dependencies change
    setSelectedAds([]);
  }, [selectedAdAccount, selectedPlatform, selectedCampaigns, selectedAdSets]);
  
  // DV360: Fetch insertion orders when platform/ad account/campaigns change
  useEffect(() => {
    if (selectedPlatform === 'DV360') {
      fetchInsertionOrders();
      setSelectedInsertionOrders([]);
    }
  }, [selectedAdAccount, selectedPlatform, selectedCampaigns]);
  
  // DV360: Fetch line items when insertion orders change
  useEffect(() => {
    if (selectedPlatform === 'DV360') {
      fetchLineItems();
      setSelectedLineItems([]);
    }
  }, [selectedAdAccount, selectedPlatform, selectedCampaigns, selectedInsertionOrders]);
  
  // Update column order when metrics change
  // Date is always first, Campaign Name always second, then Ad Set, then Ad, then breakdown, then metrics
  useEffect(() => {
    const columns = [];
    
    // Date is always first if included
    if (includeDate) {
      columns.push({ id: 'date', label: 'Date', type: 'text', fixed: true });
    }
    
    // Campaign Name is always second if included
    if (includeCampaignName) {
      columns.push({ id: 'campaign_name', label: 'Campaign Name', type: 'text', fixed: true });
    }
    
    // Ad Set Name and Ad Name are now selectable metrics
    // Only add them to columns if they are selected in metrics
    // They will be added via the metricCols below
    
    // Add breakdown column if it's not date, campaign (to avoid duplicates)
    if (selectedBreakdown !== 'date' && selectedBreakdown !== 'campaign') {
      const breakdownCol = { 
        id: selectedBreakdown, 
        label: availableBreakdowns.find(b => b.id === selectedBreakdown)?.label || selectedBreakdown, 
        type: 'text',
        fixed: false
      };
      columns.push(breakdownCol);
    }
    
    // Add metric columns (excluding date if already added, and ad_set_name/ad_name which go after campaign)
    const metricCols = selectedMetrics
      .filter(m => m !== 'date') // Don't add date twice
      .map(m => {
        const metric = availableMetrics.find(am => am.id === m);
        return { id: m, label: metric?.label || m, type: metric?.type || 'number', fixed: false };
      });
    
    setColumnOrder([...columns, ...metricCols]);
  }, [selectedMetrics, selectedBreakdown, availableMetrics, availableBreakdowns, includeDate, includeCampaignName]);
  
  // Reset page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [reportData]);
  
  // Auto resync every 10 minutes
  useEffect(() => {
    if (!autoResyncEnabled || !selectedPlatform || selectedPlatform === 'All Platforms') {
      return;
    }
    
    // Set next sync time
    const updateNextSyncTime = () => {
      setNextAutoSync(new Date(Date.now() + AUTO_RESYNC_INTERVAL));
    };
    
    updateNextSyncTime();
    
    const autoResync = async () => {
      if (resyncing) return; // Don't sync if already syncing
      
      console.log(`Auto resync triggered at ${new Date().toLocaleTimeString()}`);
      setResyncing(true);
      
      try {
        const response = await api.post('/sync-advertiser-data', null, {
          params: { platform: selectedPlatform },
          timeout: 120000
        });
        
        const result = response.data;
        if (result.success) {
          setLastAutoSync(new Date());
          toast.info(`Auto-synced: ${result.performance_records_synced} records`, { duration: 3000 });
          fetchSyncStatus();
          
          // Refresh report if data exists
          if (reportData.length > 0) {
            generateReport();
          }
        }
      } catch (error) {
        console.error('Auto resync failed:', error);
        // Silent fail for auto-sync, don't show error toast
      } finally {
        setResyncing(false);
        updateNextSyncTime();
      }
    };
    
    // Set up interval for auto resync
    const intervalId = setInterval(autoResync, AUTO_RESYNC_INTERVAL);
    
    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [autoResyncEnabled, selectedPlatform]);

  const fetchSyncStatus = async () => {
    if (selectedPlatform === 'All Platforms') {
      setSyncStatus(null);
      return;
    }
    try {
      const response = await api.get('/sync-status', { params: { platform: selectedPlatform } });
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
      setSyncStatus(null);
    }
  };
  
  const fetchCampaigns = async () => {
    try {
      const params = {};
      if (selectedPlatform !== 'All Platforms') {
        params.platforms = selectedPlatform;
      }
      if (selectedAdAccount !== 'All Ad Accounts') {
        params.ad_account_name = selectedAdAccount;
      }
      const response = await api.get('/campaigns', { params });
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };
  
  const fetchAdAccounts = async () => {
    try {
      const params = {};
      if (selectedPlatform !== 'All Platforms') {
        params.platform = selectedPlatform;
      }
      const response = await api.get('/ad-accounts', { params });
      setAdAccounts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch ad accounts:', error);
      setAdAccounts([]);
    }
  };
  
  const fetchAdSets = async () => {
    try {
      const params = {};
      if (selectedPlatform !== 'All Platforms') {
        params.platform = selectedPlatform;
      }
      if (selectedAdAccount !== 'All Ad Accounts') {
        params.ad_account_name = selectedAdAccount;
      }
      // Filter by selected campaigns
      if (selectedCampaigns.length > 0) {
        params.campaign_ids = selectedCampaigns.join(',');
      }
      const response = await api.get('/ad-sets', { params });
      setAdSets(response.data || []);
    } catch (error) {
      console.error('Failed to fetch ad sets:', error);
      setAdSets([]);
    }
  };
  
  const fetchAds = async () => {
    try {
      const params = {};
      if (selectedPlatform !== 'All Platforms') {
        params.platform = selectedPlatform;
      }
      if (selectedAdAccount !== 'All Ad Accounts') {
        params.ad_account_name = selectedAdAccount;
      }
      // Filter by selected campaigns
      if (selectedCampaigns.length > 0) {
        params.campaign_ids = selectedCampaigns.join(',');
      }
      // Filter by selected ad sets
      if (selectedAdSets.length > 0) {
        params.ad_set_names = selectedAdSets.join(',');
      }
      const response = await api.get('/ads', { params });
      setAds(response.data || []);
    } catch (error) {
      console.error('Failed to fetch ads:', error);
      setAds([]);
    }
  };
  
  // DV360-specific: Fetch insertion orders
  const fetchInsertionOrders = async () => {
    if (selectedPlatform !== 'DV360') {
      setInsertionOrders([]);
      return;
    }
    try {
      const params = { platform: 'DV360' };
      if (selectedAdAccount !== 'All Ad Accounts') {
        params.ad_account_name = selectedAdAccount;
      }
      if (selectedCampaigns.length > 0) {
        params.campaign_ids = selectedCampaigns.join(',');
      }
      const response = await api.get('/insertion-orders', { params });
      setInsertionOrders(response.data || []);
    } catch (error) {
      console.error('Failed to fetch insertion orders:', error);
      setInsertionOrders([]);
    }
  };
  
  // DV360-specific: Fetch line items
  const fetchLineItems = async () => {
    if (selectedPlatform !== 'DV360') {
      setLineItems([]);
      return;
    }
    try {
      const params = { platform: 'DV360' };
      if (selectedAdAccount !== 'All Ad Accounts') {
        params.ad_account_name = selectedAdAccount;
      }
      if (selectedCampaigns.length > 0) {
        params.campaign_ids = selectedCampaigns.join(',');
      }
      if (selectedInsertionOrders.length > 0) {
        params.insertion_order_names = selectedInsertionOrders.join(',');
      }
      const response = await api.get('/line-items', { params });
      setLineItems(response.data || []);
    } catch (error) {
      console.error('Failed to fetch line items:', error);
      setLineItems([]);
    }
  };
  
  const generateReport = async () => {
    setGenerating(true);
    try {
      const params = {
        start_date: format(dateRange.from, 'yyyy-MM-dd'),
        end_date: format(dateRange.to, 'yyyy-MM-dd'),
        breakdown: selectedBreakdown,
        metrics: selectedMetrics.join(','),
      };
      
      if (selectedPlatform !== 'All Platforms') {
        params.platforms = selectedPlatform;
      }
      
      if (selectedAdAccount !== 'All Ad Accounts') {
        params.ad_account_name = selectedAdAccount;
      }
      
      if (selectedCampaigns.length > 0) {
        params.campaign_ids = selectedCampaigns.join(',');
      }
      
      // Add ad set and ad filters (Meta)
      if (selectedAdSets.length > 0) {
        params.ad_set_names = selectedAdSets.join(',');
      }
      
      if (selectedAds.length > 0) {
        params.ad_names = selectedAds.join(',');
      }
      
      // Add DV360-specific filters
      if (selectedPlatform === 'DV360') {
        if (selectedInsertionOrders.length > 0) {
          params.insertion_order_names = selectedInsertionOrders.join(',');
        }
        if (selectedLineItems.length > 0) {
          params.line_item_names = selectedLineItems.join(',');
        }
      }
      
      // Add include flags for ad_set_name and ad_name columns
      if (selectedMetrics.includes('ad_set_name')) {
        params.include_ad_set_name = 'true';
      }
      if (selectedMetrics.includes('ad_name')) {
        params.include_ad_name = 'true';
      }
      
      const response = await api.get('/performance-report', { params });
      setReportData(response.data?.data || []);
      setTotalRows(response.data?.total_rows || 0);
      
      // Show warning if breakdown data not available
      if (response.data?.warning) {
        toast.warning(response.data.warning, { duration: 8000 });
      } else {
        toast.success(`Generated report with ${response.data?.total_rows || 0} rows`);
      }
    } catch (error) {
      toast.error('Failed to generate report');
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };
  
  const resyncMetaData = async () => {
    setResyncing(true);
    try {
      // Use synchronous sync endpoint for immediate feedback
      const response = await api.post('/sync-advertiser-data', null, {
        params: { platform: selectedPlatform },
        timeout: 120000  // 2 minute timeout for sync
      });
      const result = response.data;
      
      if (result.success) {
        toast.success(`Synced ${result.campaigns_synced} campaigns and ${result.performance_records_synced} records!`);
        
        // Show breakdown data info
        const breakdownInfo = [];
        if (result.records_with_age_breakdown > 0) breakdownInfo.push(`Age: ${result.records_with_age_breakdown}`);
        if (result.records_with_gender_breakdown > 0) breakdownInfo.push(`Gender: ${result.records_with_gender_breakdown}`);
        if (result.records_with_country_breakdown > 0) breakdownInfo.push(`Country: ${result.records_with_country_breakdown}`);
        if (result.records_with_region_breakdown > 0) breakdownInfo.push(`Region: ${result.records_with_region_breakdown}`);
        
        if (breakdownInfo.length > 0) {
          toast.info(`Breakdown data synced: ${breakdownInfo.join(', ')}`, { duration: 6000 });
        } else if (result.performance_records_synced > 0) {
          toast.warning('No demographic/geographic breakdown data available. Check Meta app permissions.', {
            duration: 8000,
          });
        }
        
        // Refresh sync status
        fetchSyncStatus();
      } else {
        toast.info(result.message || 'Sync completed');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to sync data';
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setResyncing(false);
    }
  };
  
  const fixPlatformData = async () => {
    setFixingData(true);
    try {
      const response = await api.post('/fix-advertiser-platform-data');
      const result = response.data;
      if (result.updated_count > 0) {
        toast.success(`Fixed ${result.updated_count} data records. Please regenerate your report.`);
      } else {
        toast.info('No orphaned data found to fix. Try clicking "Resync Data" first.');
      }
    } catch (error) {
      toast.error('Failed to fix data');
      console.error(error);
    } finally {
      setFixingData(false);
    }
  };
  
  const fixCurrencyData = async () => {
    if (!selectedPlatform || selectedPlatform === 'All Platforms') {
      toast.error('Please select a specific platform first');
      return;
    }
    
    setFixingCurrency(true);
    try {
      const response = await api.post('/fix-currency-data', null, {
        params: { platform: selectedPlatform }
      });
      const result = response.data;
      if (result.success) {
        toast.success(`Fixed currency for ${result.updated_records} records. ${result.records_with_currency}/${result.total_records} now have currency.`);
        
        // Show currency map
        if (result.currency_map && Object.keys(result.currency_map).length > 0) {
          const currencies = [...new Set(Object.values(result.currency_map))];
          toast.info(`Currencies found: ${currencies.join(', ')}`, { duration: 5000 });
        }
        
        // Refresh report if data exists
        if (reportData.length > 0) {
          generateReport();
        }
      } else {
        toast.info('No records needed currency update');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix currency data');
      console.error(error);
    } finally {
      setFixingCurrency(false);
    }
  };
  
  const diagnoseIntegration = async () => {
    if (selectedPlatform !== 'Meta') {
      toast.info('Diagnosis is currently only available for Meta integrations.');
      return;
    }
    
    setDiagnosing(true);
    setDiagnosisResult(null);
    try {
      const response = await api.get('/diagnose-meta-integration');
      const result = response.data;
      setDiagnosisResult(result);
      
      if (result.age_breakdown_working) {
        toast.success('Meta integration is working correctly! Age breakdown data is available.');
      } else if (result.issues && result.issues.length > 0) {
        toast.error(`Found ${result.issues.length} issue(s) with your Meta integration.`);
      }
    } catch (error) {
      toast.error('Failed to diagnose integration');
      console.error(error);
    } finally {
      setDiagnosing(false);
    }
  };
  
  // Template Functions
  const fetchTemplates = async () => {
    try {
      const response = await api.get('/report-templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };
  
  // Filter templates based on search query
  const filteredTemplates = useMemo(() => {
    if (!templateSearchQuery.trim()) return templates;
    const query = templateSearchQuery.toLowerCase();
    return templates.filter(template => 
      template.name?.toLowerCase().includes(query) ||
      template.platform?.toLowerCase().includes(query) ||
      template.breakdown?.toLowerCase().includes(query)
    );
  }, [templates, templateSearchQuery]);
  
  useEffect(() => {
    fetchTemplates();
  }, []);
  
  const saveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setSavingTemplate(true);
    try {
      const templateData = {
        name: templateName.trim(),
        platform: selectedPlatform,
        ad_account: selectedAdAccount,
        breakdown: selectedBreakdown,
        metrics: selectedMetrics,
        include_date: includeDate,
        include_campaign_name: includeCampaignName,
        column_order: columnOrder.map(c => c.id),
      };
      
      await api.post('/report-templates', templateData);
      toast.success(`Template "${templateName}" saved successfully!`);
      setShowSaveTemplateDialog(false);
      setTemplateName('');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
      console.error(error);
    } finally {
      setSavingTemplate(false);
    }
  };
  
  const loadTemplate = (template) => {
    setSelectedPlatform(template.platform || 'All Platforms');
    setSelectedAdAccount(template.ad_account || 'All Ad Accounts');
    setSelectedBreakdown(template.breakdown || 'campaign');
    setSelectedMetrics(template.metrics || ['date', 'impressions', 'clicks', 'spend']);
    setIncludeDate(template.include_date !== false);
    setIncludeCampaignName(template.include_campaign_name !== false);
    setSelectedTemplate(template);
    setShowLoadTemplateDialog(false);
    toast.success(`Template "${template.name}" loaded. Adjust date range and generate report.`);
  };
  
  const deleteTemplate = async (templateId, templateName) => {
    if (!confirm(`Are you sure you want to delete template "${templateName}"?`)) {
      return;
    }
    
    try {
      await api.delete(`/report-templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
      console.error(error);
    }
  };
  
  const formatSyncTime = (isoString) => {
    if (!isoString) return 'Never';
    try {
      const date = new Date(isoString);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch {
      return 'Unknown';
    }
  };
  
  const exportCSV = () => {
    if (sortedData.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    // Build CSV with column order
    const headers = columnOrder.map(c => c.label);
    let csv = 'Report PRO - Performance Report\n';
    csv += `Generated: ${format(new Date(), 'PPpp')}\n`;
    csv += `Platform: ${selectedPlatform}\n`;
    csv += `Date Range: ${format(dateRange.from, 'PP')} - ${format(dateRange.to, 'PP')}\n`;
    csv += `Breakdown: ${availableBreakdowns.find(b => b.id === selectedBreakdown)?.label}\n`;
    csv += `Total Rows: ${sortedData.length}\n\n`;
    csv += headers.join(',') + '\n';
    
    sortedData.forEach(row => {
      // Get currency for this row
      const rowCurrency = row.currency || advertiserCurrency;
      const currencySymbol = SUPPORTED_CURRENCIES[rowCurrency]?.symbol || '$';
      
      const values = columnOrder.map(col => {
        let value = row[col.id];
        if (col.type === 'currency') {
          value = typeof value === 'number' ? `${currencySymbol}${value.toFixed(2)}` : value;
        } else if (col.type === 'percent') {
          value = typeof value === 'number' ? value.toFixed(2) + '%' : value;
        }
        // Escape commas and quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      });
      csv += values.join(',') + '\n';
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };
  
  const handleSort = (columnId) => {
    if (sortColumn === columnId) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnId);
      setSortDirection('desc');
    }
  };
  
  const moveColumn = (index, direction) => {
    if (index === 0) return; // Don't move breakdown column
    const newOrder = [...columnOrder];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 1 || newIndex >= newOrder.length) return;
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    setColumnOrder(newOrder);
  };
  
  const toggleMetric = (metricId) => {
    setSelectedMetrics(prev => 
      prev.includes(metricId) 
        ? prev.filter(m => m !== metricId)
        : [...prev, metricId]
    );
  };
  
  const toggleCampaign = (campaignId) => {
    setSelectedCampaigns(prev =>
      prev.includes(campaignId)
        ? prev.filter(c => c !== campaignId)
        : [...prev, campaignId]
    );
  };
  
  const selectAllCampaigns = () => {
    setSelectedCampaigns(filteredCampaigns.map(c => c.id));
  };
  
  const clearAllCampaigns = () => {
    setSelectedCampaigns([]);
  };
  
  // Ad Set toggle functions
  const toggleAdSet = (adSetName) => {
    setSelectedAdSets(prev =>
      prev.includes(adSetName)
        ? prev.filter(a => a !== adSetName)
        : [...prev, adSetName]
    );
  };
  
  const selectAllAdSets = () => {
    setSelectedAdSets(filteredAdSets.map(a => a.name));
  };
  
  const clearAllAdSets = () => {
    setSelectedAdSets([]);
  };
  
  // Ad toggle functions
  const toggleAd = (adName) => {
    setSelectedAds(prev =>
      prev.includes(adName)
        ? prev.filter(a => a !== adName)
        : [...prev, adName]
    );
  };
  
  const selectAllAds = () => {
    setSelectedAds(filteredAds.map(a => a.name));
  };
  
  const clearAllAds = () => {
    setSelectedAds([]);
  };
  
  // DV360: Insertion Order toggle functions
  const toggleInsertionOrder = (ioName) => {
    setSelectedInsertionOrders(prev =>
      prev.includes(ioName)
        ? prev.filter(io => io !== ioName)
        : [...prev, ioName]
    );
  };
  
  const selectAllInsertionOrders = () => {
    setSelectedInsertionOrders(filteredInsertionOrders.map(io => io.name));
  };
  
  const clearAllInsertionOrders = () => {
    setSelectedInsertionOrders([]);
  };
  
  // DV360: Line Item toggle functions
  const toggleLineItem = (liName) => {
    setSelectedLineItems(prev =>
      prev.includes(liName)
        ? prev.filter(li => li !== liName)
        : [...prev, liName]
    );
  };
  
  const selectAllLineItems = () => {
    setSelectedLineItems(filteredLineItems.map(li => li.name));
  };
  
  const clearAllLineItems = () => {
    setSelectedLineItems([]);
  };
  
  // Filtered DV360 lists
  const filteredInsertionOrders = useMemo(() => {
    if (!insertionOrderSearch) return insertionOrders;
    const search = insertionOrderSearch.toLowerCase();
    return insertionOrders.filter(io => 
      io.name?.toLowerCase().includes(search)
    );
  }, [insertionOrders, insertionOrderSearch]);
  
  const filteredLineItems = useMemo(() => {
    let filtered = lineItems;
    
    // Filter by selected insertion orders
    if (selectedInsertionOrders.length > 0) {
      filtered = filtered.filter(li => selectedInsertionOrders.includes(li.insertion_order_name));
    }
    
    // Filter by search
    if (lineItemSearch) {
      const search = lineItemSearch.toLowerCase();
      filtered = filtered.filter(li => 
        li.name?.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  }, [lineItems, lineItemSearch, selectedInsertionOrders]);
  
  // Filtered ad accounts list
  const filteredAdAccounts = useMemo(() => {
    if (!adAccountSearch) return adAccounts;
    const search = adAccountSearch.toLowerCase();
    return adAccounts.filter(aa => 
      aa.name?.toLowerCase().includes(search)
    );
  }, [adAccounts, adAccountSearch]);
  
  // Drag and drop handlers for columns
  const handleDragStart = (e, index) => {
    setDraggedColumn(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };
  
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedColumn(null);
    setDragOverColumn(null);
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === index) return;
    setDragOverColumn(index);
  };
  
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === dropIndex) return;
    
    const newOrder = [...columnOrder];
    const [draggedItem] = newOrder.splice(draggedColumn, 1);
    newOrder.splice(dropIndex, 0, draggedItem);
    setColumnOrder(newOrder);
    
    setDraggedColumn(null);
    setDragOverColumn(null);
  };
  
  const formatValue = (value, type, rowCurrency = null) => {
    if (value === null || value === undefined) return '-';
    switch (type) {
      case 'currency':
        // Use row-level currency if available, otherwise fall back to advertiser currency
        const currencyToUse = rowCurrency || advertiserCurrency;
        return formatCurrency(value, currencyToUse);
      case 'percent':
        return `${Number(value).toFixed(2)}%`;
      case 'decimal':
        return Number(value).toFixed(2);
      case 'number':
        return formatNumberExact(value);  // Use exact numbers, no K/M abbreviation
      default:
        return value;
    }
  };
  
  // Calculate totals for the report
  const totals = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return null;
    
    const sum = (key) => sortedData.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
    const avg = (key) => {
      const total = sum(key);
      return sortedData.length > 0 ? total / sortedData.length : 0;
    };
    
    // Get most common currency for totals display
    const currencyCounts = {};
    sortedData.forEach(row => {
      const curr = row.currency || 'USD';
      currencyCounts[curr] = (currencyCounts[curr] || 0) + 1;
    });
    const primaryCurrency = Object.entries(currencyCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'USD';
    
    const totalImpressions = sum('impressions');
    const totalClicks = sum('clicks');
    const totalSpend = sum('spend');
    const totalConversions = sum('conversions');
    const totalReach = sum('reach');
    const totalLinkClicks = sum('link_clicks');
    const totalVideoViews = sum('video_views');
    const totalPostEngagement = sum('post_engagement');
    
    return {
      impressions: totalImpressions,
      clicks: totalClicks,
      spend: totalSpend,
      currency: primaryCurrency,
      ctr: totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0,
      cpc: totalClicks > 0 ? (totalSpend / totalClicks) : 0,
      cpm: totalImpressions > 0 ? (totalSpend / totalImpressions * 1000) : 0,
      conversions: totalConversions,
      reach: totalReach,
      frequency: avg('frequency'),
      link_clicks: totalLinkClicks,
      link_ctr: totalImpressions > 0 ? (totalLinkClicks / totalImpressions * 100) : 0,
      cost_per_link_click: totalLinkClicks > 0 ? (totalSpend / totalLinkClicks) : 0,
      video_views: totalVideoViews,
      post_engagement: totalPostEngagement,
      conversion_rate: totalClicks > 0 ? (totalConversions / totalClicks * 100) : 0,
      cost_per_conversion: totalConversions > 0 ? (totalSpend / totalConversions) : 0,
    };
  }, [sortedData]);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/performance-report')}
                className="text-slate-600 hover:text-slate-900"
                data-testid="back-to-platforms"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Platforms
              </Button>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-6 h-6" />
              {platformFromUrl !== 'All Platforms' ? `${platformFromUrl} Performance Report` : 'Performance Report'}
            </h1>
            <p className="text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
              {platformFromUrl !== 'All Platforms' 
                ? `Analyze ${platformFromUrl} campaign performance with platform-specific metrics`
                : 'Generate custom performance reports with flexible metrics and breakdowns'}
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                Currency: Per Ad Account
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Template Buttons */}
            <Button
              variant="outline"
              onClick={() => setShowLoadTemplateDialog(true)}
              data-testid="load-template-btn"
              title="Load saved template"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Load Template
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowSaveTemplateDialog(true)}
              data-testid="save-template-btn"
              title="Save current settings as template"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Template
            </Button>
            
            {selectedPlatform !== 'All Platforms' && (user?.role === 'advertiser' || user?.role === 'user') && (
              <>
                <Button
                  variant="outline"
                  onClick={resyncMetaData}
                  disabled={resyncing}
                  data-testid="resync-data-btn"
                  title={`Sync ${selectedPlatform} data from your integration`}
                >
                  {resyncing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sync Data
                    </>
                  )}
                </Button>
              </>
            )}
            <Button
              variant="outline"
              onClick={exportCSV}
              disabled={reportData.length === 0}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Sync Status Banner */}
        {syncStatus && selectedPlatform !== 'All Platforms' && (
          <div 
            className={cn(
              "flex items-center justify-between px-4 py-2 rounded-lg text-sm",
              syncStatus.has_integrations
                ? syncStatus.last_sync_status === 'success'
                  ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                  : syncStatus.last_sync_status === 'error'
                  ? "bg-red-50 border border-red-200 text-red-800"
                  : "bg-slate-50 border border-slate-200 text-slate-600"
                : "bg-amber-50 border border-amber-200 text-amber-800"
            )}
            data-testid="sync-status-banner"
          >
            <div className="flex items-center gap-2">
              {syncStatus.has_integrations ? (
                <>
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    syncStatus.last_sync_status === 'success' ? "bg-emerald-500" :
                    syncStatus.last_sync_status === 'error' ? "bg-red-500" : "bg-slate-400"
                  )} />
                  <span className="font-medium">{selectedPlatform} Sync Status:</span>
                  <span>
                    {syncStatus.last_sync_status === 'success' ? 'Last synced' : 
                     syncStatus.last_sync_status === 'error' ? 'Sync failed' : 'Pending'} 
                    {syncStatus.last_sync && ` - ${formatSyncTime(syncStatus.last_sync)}`}
                  </span>
                  {syncStatus.total_integrations > 1 && (
                    <span className="text-xs opacity-75">
                      ({syncStatus.successful_syncs}/{syncStatus.total_integrations} integrations synced)
                    </span>
                  )}
                </>
              ) : (
                <>
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span>No active {selectedPlatform} integrations found. Set up an integration in Advertisers → Integrations.</span>
                </>
              )}
            </div>
            {syncStatus.has_integrations && syncStatus.last_sync_status === 'error' && syncStatus.last_sync_error && (
              <span className="text-xs truncate max-w-xs" title={syncStatus.last_sync_error}>
                Error: {syncStatus.last_sync_error.substring(0, 50)}...
              </span>
            )}
          </div>
        )}
        
        {/* Auto Resync Status */}
        {selectedPlatform && selectedPlatform !== 'All Platforms' && (
          <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <RefreshCw className={cn("w-4 h-4 text-blue-600", resyncing && "animate-spin")} />
                <span className="font-medium text-blue-800">Auto Resync:</span>
                <button
                  onClick={() => setAutoResyncEnabled(!autoResyncEnabled)}
                  className={cn(
                    "px-2 py-0.5 rounded text-xs font-medium transition-colors",
                    autoResyncEnabled 
                      ? "bg-blue-600 text-white" 
                      : "bg-slate-200 text-slate-600"
                  )}
                >
                  {autoResyncEnabled ? 'ON' : 'OFF'}
                </button>
              </div>
              {autoResyncEnabled && (
                <span className="text-blue-600 text-xs">
                  {resyncing ? 'Syncing...' : 
                   nextAutoSync ? `Next sync in ${Math.ceil((nextAutoSync - Date.now()) / 60000)} min` : 'Every 10 min'}
                </span>
              )}
              {lastAutoSync && (
                <span className="text-blue-500 text-xs">
                  • Last auto-sync: {lastAutoSync.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Diagnosis Results Panel */}
        {diagnosisResult && selectedPlatform === 'Meta' && (
          <div className={cn(
            "p-4 rounded-lg border text-sm",
            diagnosisResult.age_breakdown_working 
              ? "bg-emerald-50 border-emerald-200"
              : "bg-red-50 border-red-200"
          )}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 font-medium">
                {diagnosisResult.age_breakdown_working ? (
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                <span>Meta Integration Diagnosis</span>
              </div>
              <button 
                onClick={() => setDiagnosisResult(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {diagnosisResult.issues && diagnosisResult.issues.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className="font-medium text-red-700">Issues Found:</p>
                <ul className="list-disc list-inside text-red-600 space-y-1">
                  {diagnosisResult.issues.map((issue, idx) => (
                    <li key={idx}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {diagnosisResult.token_check && (
              <div className="mt-3 text-xs space-y-1 text-slate-600">
                <p>Token Valid: {diagnosisResult.token_check.token_valid ? '✓ Yes' : '✗ No'}</p>
                {diagnosisResult.token_check.scopes && (
                  <p>Permissions: {diagnosisResult.token_check.scopes.join(', ') || 'None'}</p>
                )}
                {diagnosisResult.token_check.missing_permissions?.length > 0 && (
                  <p className="text-red-600">Missing: {diagnosisResult.token_check.missing_permissions.join(', ')}</p>
                )}
              </div>
            )}
            
            {diagnosisResult.age_breakdown_working && (
              <p className="mt-3 text-emerald-700">
                ✓ Age breakdown is working! You can use the Age breakdown in your reports.
              </p>
            )}
            
            {!diagnosisResult.age_breakdown_working && (
              <p className="mt-3 text-red-700 text-xs">
                To fix: Reconnect your Meta integration with the required permissions (ads_read).
              </p>
            )}
          </div>
        )}

        {/* Filters Card */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Report Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Row 1: Platform + Ad Account */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Platform Select - Only show if on "All Platforms" page */}
              {platformFromUrl === 'All Platforms' && (
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                    <SelectTrigger data-testid="platform-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLATFORMS.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Platform Badge - Show if on platform-specific page */}
              {platformFromUrl !== 'All Platforms' && (
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <div className="h-10 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 flex items-center">
                    <span className="font-medium text-slate-700">{platformFromUrl}</span>
                  </div>
                </div>
              )}

              {/* Ad Account / Advertiser Select with Search */}
              <div className="space-y-2">
                <Label>{selectedPlatform === 'DV360' ? 'Advertiser' : 'Ad Account'}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                      data-testid="ad-account-select"
                    >
                      <span className="truncate">
                        {selectedAdAccount === 'All Ad Accounts' 
                          ? (selectedPlatform === 'DV360' ? 'All Advertisers' : 'All Ad Accounts')
                          : selectedAdAccount}
                      </span>
                      <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder={`Search ${selectedPlatform === 'DV360' ? 'advertisers' : 'ad accounts'}...`}
                          value={adAccountSearch}
                          onChange={(e) => setAdAccountSearch(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border rounded-md"
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto p-2">
                      <label
                        className={cn(
                          "flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer",
                          selectedAdAccount === 'All Ad Accounts' && "bg-blue-50"
                        )}
                        onClick={() => {
                          setSelectedAdAccount('All Ad Accounts');
                          setSelectedCampaigns([]);
                        }}
                      >
                        <span className="text-sm font-medium">
                          {selectedPlatform === 'DV360' ? 'All Advertisers' : 'All Ad Accounts'}
                        </span>
                      </label>
                      {filteredAdAccounts.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">
                          No {selectedPlatform === 'DV360' ? 'advertisers' : 'ad accounts'} found
                        </p>
                      ) : (
                        filteredAdAccounts.map(aa => (
                          <label
                            key={aa.name}
                            className={cn(
                              "flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer",
                              selectedAdAccount === aa.name && "bg-blue-50"
                            )}
                            onClick={() => {
                              setSelectedAdAccount(aa.name);
                              setSelectedCampaigns([]);
                            }}
                          >
                            <span className="text-sm truncate">
                              {aa.name} {aa.platform && selectedPlatform === 'All Platforms' ? `(${aa.platform})` : ''}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Campaigns Multi-Select */}
              <div className="space-y-2">
                <Label>Campaigns</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                      data-testid="campaigns-select-btn"
                    >
                      <span className="truncate">
                        {selectedCampaigns.length === 0 
                          ? 'All Campaigns' 
                          : `${selectedCampaigns.length} selected`}
                      </span>
                      <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search campaigns..."
                          value={campaignSearch}
                          onChange={(e) => setCampaignSearch(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border rounded-md"
                        />
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" onClick={selectAllCampaigns}>
                          Select All
                        </Button>
                        <Button size="sm" variant="outline" onClick={clearAllCampaigns}>
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto p-2">
                      {filteredCampaigns.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">No campaigns found</p>
                      ) : (
                        filteredCampaigns.map(campaign => (
                          <label
                            key={campaign.id}
                            className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedCampaigns.includes(campaign.id)}
                              onCheckedChange={() => toggleCampaign(campaign.id)}
                            />
                            <span className="text-sm truncate flex-1">{campaign.name}</span>
                            <span className="text-xs text-slate-400">
                              {campaign.platforms?.[0] || campaign.platform}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Row 2: Ad Set + Ads (Cascading) - Only for Meta platform */}
            {selectedPlatform === 'Meta' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Ad Set Multi-Select */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Ad Sets
                  {selectedCampaigns.length > 0 && (
                    <span className="text-xs text-blue-600">(filtered by selected campaigns)</span>
                  )}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                      data-testid="adsets-select-btn"
                    >
                      <span className="truncate">
                        {selectedAdSets.length === 0 
                          ? 'All Ad Sets' 
                          : `${selectedAdSets.length} selected`}
                      </span>
                      <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search ad sets..."
                          value={adSetSearch}
                          onChange={(e) => setAdSetSearch(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border rounded-md"
                        />
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" onClick={selectAllAdSets}>
                          Select All
                        </Button>
                        <Button size="sm" variant="outline" onClick={clearAllAdSets}>
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto p-2">
                      {filteredAdSets.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">
                          {adSets.length === 0 
                            ? selectedCampaigns.length > 0 
                              ? 'No ad sets for selected campaigns' 
                              : 'No ad sets available. Sync ad-level data first.'
                            : 'No ad sets found'}
                        </p>
                      ) : (
                        filteredAdSets.map(adSet => (
                          <label
                            key={adSet.id}
                            className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedAdSets.includes(adSet.name)}
                              onCheckedChange={() => toggleAdSet(adSet.name)}
                            />
                            <span className="text-sm truncate flex-1">{adSet.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Ad Multi-Select */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Ads
                  {(selectedCampaigns.length > 0 || selectedAdSets.length > 0) && (
                    <span className="text-xs text-blue-600">
                      (filtered by {selectedAdSets.length > 0 ? 'selected ad sets' : 'selected campaigns'})
                    </span>
                  )}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                      data-testid="ads-select-btn"
                    >
                      <span className="truncate">
                        {selectedAds.length === 0 
                          ? 'All Ads' 
                          : `${selectedAds.length} selected`}
                      </span>
                      <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search ads..."
                          value={adSearch}
                          onChange={(e) => setAdSearch(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border rounded-md"
                        />
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" onClick={selectAllAds}>
                          Select All
                        </Button>
                        <Button size="sm" variant="outline" onClick={clearAllAds}>
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto p-2">
                      {filteredAds.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">
                          {ads.length === 0 
                            ? selectedAdSets.length > 0
                              ? 'No ads for selected ad sets'
                              : selectedCampaigns.length > 0 
                                ? 'No ads for selected campaigns' 
                                : 'No ads available. Sync ad-level data first.'
                            : 'No ads found'}
                        </p>
                      ) : (
                        filteredAds.map(ad => (
                          <label
                            key={ad.id}
                            className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedAds.includes(ad.name)}
                              onCheckedChange={() => toggleAd(ad.name)}
                            />
                            <span className="text-sm truncate flex-1">{ad.name}</span>
                            {ad.ad_set_name && (
                              <span className="text-xs text-slate-400 truncate max-w-24" title={ad.ad_set_name}>
                                {ad.ad_set_name}
                              </span>
                            )}
                          </label>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            )}

            {/* Row 2: Insertion Order + Line Item (Cascading) - Only for DV360 platform */}
            {selectedPlatform === 'DV360' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Insertion Order Multi-Select */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Insertion Orders
                  {selectedCampaigns.length > 0 && (
                    <span className="text-xs text-blue-600">(filtered by selected campaigns)</span>
                  )}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                      data-testid="insertion-orders-select-btn"
                    >
                      <span className="truncate">
                        {selectedInsertionOrders.length === 0 
                          ? 'All Insertion Orders' 
                          : `${selectedInsertionOrders.length} selected`}
                      </span>
                      <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search insertion orders..."
                          value={insertionOrderSearch}
                          onChange={(e) => setInsertionOrderSearch(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border rounded-md"
                        />
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" onClick={selectAllInsertionOrders}>
                          Select All
                        </Button>
                        <Button size="sm" variant="outline" onClick={clearAllInsertionOrders}>
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto p-2">
                      {filteredInsertionOrders.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">
                          {insertionOrders.length === 0 
                            ? 'No insertion orders available'
                            : 'No insertion orders found'}
                        </p>
                      ) : (
                        filteredInsertionOrders.map((io, idx) => (
                          <label
                            key={`${io.name}-${idx}`}
                            className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedInsertionOrders.includes(io.name)}
                              onCheckedChange={() => toggleInsertionOrder(io.name)}
                            />
                            <span className="text-sm truncate flex-1">{io.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Line Item Multi-Select */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Line Items
                  {selectedInsertionOrders.length > 0 && (
                    <span className="text-xs text-blue-600">(filtered by selected insertion orders)</span>
                  )}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                      data-testid="line-items-select-btn"
                    >
                      <span className="truncate">
                        {selectedLineItems.length === 0 
                          ? 'All Line Items' 
                          : `${selectedLineItems.length} selected`}
                      </span>
                      <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search line items..."
                          value={lineItemSearch}
                          onChange={(e) => setLineItemSearch(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border rounded-md"
                        />
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" onClick={selectAllLineItems}>
                          Select All
                        </Button>
                        <Button size="sm" variant="outline" onClick={clearAllLineItems}>
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto p-2">
                      {filteredLineItems.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">
                          {lineItems.length === 0 
                            ? 'No line items available'
                            : 'No line items found'}
                        </p>
                      ) : (
                        filteredLineItems.map((li, idx) => (
                          <label
                            key={`${li.name}-${idx}`}
                            className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedLineItems.includes(li.name)}
                              onCheckedChange={() => toggleLineItem(li.name)}
                            />
                            <span className="text-sm truncate flex-1">{li.name}</span>
                            {li.insertion_order_name && (
                              <span className="text-xs text-slate-400 truncate max-w-24" title={li.insertion_order_name}>
                                {li.insertion_order_name}
                              </span>
                            )}
                          </label>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            )}

            {/* Row 3: Breakdown + Date Range + Generate Button */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t">
              {/* Breakdown Select - Hide for DV360 */}
              {selectedPlatform !== 'DV360' && (
              <div className="space-y-2">
                <Label>Breakdown</Label>
                <Select value={selectedBreakdown} onValueChange={setSelectedBreakdown}>
                  <SelectTrigger data-testid="breakdown-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableBreakdowns.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.label}
                        {b.supported === false && (
                          <span className="ml-2 text-xs text-amber-500">(needs sync)</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedBreakdown === 'age' && selectedPlatform === 'Meta' && (
                  <p className="text-xs text-amber-600">
                    Age data requires synced Meta integrations.
                  </p>
                )}
                {selectedBreakdown === 'gender' && selectedPlatform === 'Meta' && (
                  <p className="text-xs text-amber-600">
                    Gender data requires synced Meta integrations.
                  </p>
                )}
                {(selectedBreakdown === 'country' || selectedBreakdown === 'region') && selectedPlatform === 'Meta' && (
                  <p className="text-xs text-amber-600">
                    Geographic data requires synced Meta integrations.
                  </p>
                )}
              </div>
              )}

              {/* Date Range */}
              <div className={cn("space-y-2", selectedPlatform === 'DV360' && "md:col-span-2")}>
                <Label>Date Range</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="date-range-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange.from && dateRange.to ? (
                        <>
                          {format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d, yyyy')}
                        </>
                      ) : (
                        'Select dates'
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => {
                        if (range?.from) {
                          // Allow single date selection - set to as the same date if not provided
                          setDateRange({ 
                            from: range.from, 
                            to: range.to || range.from 
                          });
                        }
                      }}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Generate Report Button */}
              <div className="space-y-2">
                <Label className="invisible">Generate</Label>
                <Button
                  onClick={generateReport}
                  disabled={generating}
                  className="w-full h-10 bg-blue-600 hover:bg-blue-700"
                  data-testid="generate-report-main-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Generate Report
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Fixed Columns Toggle */}
            <div className="flex items-center gap-6 pt-3 border-t">
              <Label className="text-sm font-medium">Fixed Columns:</Label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox 
                  checked={includeDate} 
                  onCheckedChange={setIncludeDate}
                  data-testid="include-date-checkbox"
                />
                <span className="text-sm">Date (1st column)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox 
                  checked={includeCampaignName} 
                  onCheckedChange={setIncludeCampaignName}
                  data-testid="include-campaign-checkbox"
                />
                <span className="text-sm">Campaign Name (2nd column)</span>
              </label>
            </div>

            {/* Metrics Selection by Category */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Metrics (click to select/deselect)</Label>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedMetrics(availableMetrics.map(m => m.id))}
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedMetrics([])}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
              {Object.entries(metricsByCategory).map(([category, metrics]) => (
                <div key={category} className="space-y-1">
                  <p className="text-xs font-medium text-slate-500 uppercase">{category}</p>
                  <div className="flex flex-wrap gap-2">
                    {metrics.map(metric => (
                      <label
                        key={metric.id}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-1.5 rounded-full border cursor-pointer transition-colors text-sm',
                          selectedMetrics.includes(metric.id)
                            ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                        )}
                      >
                        <Checkbox
                          checked={selectedMetrics.includes(metric.id)}
                          onCheckedChange={() => toggleMetric(metric.id)}
                          className="hidden"
                        />
                        {metric.label}
                        {selectedMetrics.includes(metric.id) && (
                          <X className="w-3 h-3" />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Column Arrangement & Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Column Arrangement */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <GripVertical className="w-4 h-4" />
                Column Order
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {columnOrder.map((col, index) => (
                  <div
                    key={col.id}
                    className={cn(
                      'flex items-center justify-between p-2 rounded border text-sm',
                      index === 0 ? 'bg-slate-100 border-slate-300' : 'bg-white border-slate-200'
                    )}
                  >
                    <span className="font-medium truncate">{col.label}</span>
                    {index > 0 && (
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={() => moveColumn(index, 'up')}
                          disabled={index <= 1}
                        >
                          <ArrowUp className="w-3 h-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={() => moveColumn(index, 'down')}
                          disabled={index >= columnOrder.length - 1}
                        >
                          <ArrowDown className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Report Preview / Data Table */}
          <Card className="border-slate-200 lg:col-span-4">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Table className="w-4 h-4" />
                  Report Preview
                  {totalRows > 0 && (
                    <span className="text-sm font-normal text-slate-500 ml-2">
                      ({totalRows} rows)
                    </span>
                  )}
                </CardTitle>
                
                {/* Page Size Selector */}
                {reportData.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-500">Rows per page:</Label>
                    <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="250">250</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {reportData.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Configure your report filters and click "Generate Report" to see data</p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                    <GripVertical className="w-3 h-3" />
                    Drag column headers to reorder. Click column name to sort.
                  </p>
                  <div className="overflow-auto max-h-[500px] border border-slate-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-slate-50">
                        <tr className="border-b border-slate-200">
                          {columnOrder.map((col, index) => (
                            <th
                              key={col.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, index)}
                              onDragEnd={handleDragEnd}
                              onDragOver={(e) => handleDragOver(e, index)}
                              onDrop={(e) => handleDrop(e, index)}
                              className={cn(
                                "text-left py-3 px-4 font-semibold text-slate-700 bg-slate-50 cursor-grab hover:bg-slate-100 select-none transition-colors",
                                dragOverColumn === index && "bg-blue-100 border-l-2 border-blue-500",
                                draggedColumn === index && "opacity-50"
                              )}
                            >
                              <div className="flex items-center gap-2">
                                <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <span 
                                  className="flex items-center gap-1 cursor-pointer"
                                  onClick={() => handleSort(col.id)}
                                >
                                  {col.label}
                                  {sortColumn === col.id ? (
                                    sortDirection === 'asc' ? (
                                      <ChevronUp className="w-4 h-4" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4" />
                                    )
                                  ) : (
                                    <ChevronDown className="w-4 h-4 opacity-0 hover:opacity-50" />
                                  )}
                                </span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((row, rowIndex) => (
                          <tr
                            key={rowIndex}
                            className="border-b border-slate-100 hover:bg-slate-50"
                          >
                            {columnOrder.map(col => (
                              <td key={col.id} className="py-3 px-4">
                                {formatValue(row[col.id], col.type, row.currency)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                      {/* Totals Row */}
                      {totals && (
                        <tfoot className="bg-slate-200 font-semibold border-t-2 border-slate-400 sticky bottom-0">
                          <tr>
                            {columnOrder.map((col, index) => (
                              <td key={col.id} className="py-3 px-4 bg-slate-200">
                                {index === 0 ? (
                                  <span className="font-bold text-slate-800">TOTAL</span>
                                ) : totals[col.id] !== undefined ? (
                                  <span className="font-semibold">{formatValue(totals[col.id], col.type, totals.currency)}</span>
                                ) : (
                                  '-'
                                )}
                              </td>
                            ))}
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-slate-500">
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} rows
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                        >
                          First
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm px-2">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                        >
                          Last
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* DV360 Format/Source/Domain Breakdown Tabs */}
        {selectedPlatform === 'DV360' && (
          <>
            <DV360ComprehensiveReport dateRange={dateRange} />
            <DV360BreakdownTabs dateRange={dateRange} />
          </>
        )}
      </div>

      {/* Save Template Dialog */}
      <Dialog open={showSaveTemplateDialog} onOpenChange={setShowSaveTemplateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Report Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Weekly Meta Report"
                data-testid="template-name-input"
              />
            </div>
            <div className="text-sm text-slate-500 space-y-1">
              <p className="font-medium">This template will save:</p>
              <ul className="list-disc list-inside text-xs space-y-1">
                <li>Platform: {selectedPlatform}</li>
                <li>Breakdown: {selectedBreakdown}</li>
                <li>Metrics: {selectedMetrics.length} selected</li>
                <li>Include Date: {includeDate ? 'Yes' : 'No'}</li>
                <li>Include Campaign Name: {includeCampaignName ? 'Yes' : 'No'}</li>
              </ul>
              <p className="text-xs mt-2 text-amber-600">
                Note: Date range is NOT saved. You can change dates when using the template.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTemplateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveTemplate} disabled={savingTemplate}>
              {savingTemplate ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Load Template Dialog */}
      <Dialog open={showLoadTemplateDialog} onOpenChange={(open) => {
        setShowLoadTemplateDialog(open);
        if (!open) setTemplateSearchQuery(''); // Clear search when closing
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Load Report Template</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {templates.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No saved templates yet.</p>
                <p className="text-sm">Save your current settings as a template to reuse later.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search templates..."
                    value={templateSearchQuery}
                    onChange={(e) => setTemplateSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="template-search-input"
                  />
                  {templateSearchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setTemplateSearchQuery('')}
                    >
                      <X className="w-4 h-4 text-slate-400" />
                    </Button>
                  )}
                </div>
                
                {/* Templates List */}
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {filteredTemplates.length === 0 ? (
                    <div className="text-center py-6 text-slate-500">
                      <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">No templates found matching "{templateSearchQuery}"</p>
                    </div>
                  ) : (
                    filteredTemplates.map((template) => (
                      <div
                        key={template.id}
                        className={cn(
                          "flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 cursor-pointer",
                          selectedTemplate?.id === template.id && "border-slate-900 bg-slate-50"
                        )}
                        onClick={() => loadTemplate(template)}
                      >
                        <div className="flex-1">
                          <p className="font-medium">{template.name}</p>
                          <p className="text-xs text-slate-500">
                            {template.platform} • {template.breakdown} • {template.metrics?.length || 0} metrics
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteTemplate(template.id, template.name);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                {/* Results count */}
                {templateSearchQuery && filteredTemplates.length > 0 && (
                  <p className="text-xs text-slate-500 text-center">
                    Showing {filteredTemplates.length} of {templates.length} templates
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoadTemplateDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PerformanceReport;
